import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { LimiteDeErro } from '../componentes/LimiteDeErro';
import { LogoDeca } from '../layout/LogoDeca';
import { Aviso, Botao, Campo, Cartao } from '../componentes/ui';
import { aplicarErrosDaApi } from '../hooks/erros-de-formulario';
import {
  useAutenticado,
  useEntrar,
  useMotivoEncerramento,
  usePrecisaEscolherEmpresa,
} from '../hooks/useSessao';
import { ErroDaApi } from './ErroDaApi';

/**
 * Tela de login.
 *
 * Os nomes dos campos são os do backend (`emailAddress`, `password`) porque é
 * assim que o erro por campo da API — `{ erros: [{ campo, mensagem }] }` — cai
 * embaixo do input certo, sem tabela de tradução para manter.
 *
 * Sobre as mensagens: o backend responde a mesma coisa para e-mail inexistente
 * e senha errada (RN-401), de propósito — distinguir transformaria o formulário
 * em um verificador de quem usa a plataforma. A tela repete o que ele disse, sem
 * enfeitar nem adivinhar a causa.
 */
const esquemaLogin = z.object({
  emailAddress: z
    .string()
    .trim()
    .min(1, 'Informe o e-mail')
    // Só a forma, o suficiente para evitar o round-trip óbvio. Quem decide é a
    // validação do backend (`auth.dto.ts`): repetir a regra inteira aqui criaria
    // duas definições de "e-mail válido" para divergir com o tempo.
    .email('Informe um e-mail válido'),
  // Só presença: a política de força vale para senha **nova**, não para o login.
  // Aplicá-la aqui recusaria contas legadas antes de elas terem chance de trocar
  // a senha.
  password: z.string().min(1, 'Informe a senha'),
});

type CamposLogin = z.infer<typeof esquemaLogin>;

/** Ordem de foco quando o servidor recusa: do primeiro campo para o último. */
const CAMPOS_LOGIN = ['emailAddress', 'password'] as const;

export function Login(props: { readonly destino?: string }): JSX.Element {
  const autenticado = useAutenticado();
  const precisaEscolher = usePrecisaEscolherEmpresa();
  const motivoEncerramento = useMotivoEncerramento();
  const local = useLocation();
  const login = useEntrar();

  /** Erro do último envio, cru — `ErroDaApi` decide o que dele vira texto. */
  const [erroDoEnvio, setErroDoEnvio] = useState<unknown>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CamposLogin>({
    resolver: zodResolver(esquemaLogin),
    // Quem acabou de criar a senha pelo link chega com o login preenchido.
    defaultValues: { emailAddress: emailDeOrigem(local.state) ?? '', password: '' },
    // A validação roda no envio: acusar "e-mail inválido" no terceiro caractere
    // digitado é ruído, não ajuda. Depois do primeiro erro, corrigir o campo já
    // limpa a mensagem.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const enviar = handleSubmit(async (valores) => {
    setErroDoEnvio(null);
    try {
      await login.mutateAsync({
        emailAddress: valores.emailAddress,
        password: valores.password,
      });
    } catch (erro: unknown) {
      // Nada de `throw` aqui: o `handleSubmit` propagaria a rejeição para fora
      // do React e o usuário veria o erro no console, não na tela.
      aplicarErrosDaApi<CamposLogin>(erro, CAMPOS_LOGIN, setError);
      setErroDoEnvio(erro);
    }
  });

  // RN-603 — a empresa é escolhida antes de qualquer número aparecer na tela.
  // O destino viaja junto: quem chegou por um link volta a ele depois da escolha.
  if (autenticado) {
    const destino = destinoDeOrigem(local.state) ?? props.destino ?? '/painel';
    return precisaEscolher ? (
      <Navigate to="/selecionar-empresa" replace state={{ de: destino }} />
    ) : (
      <Navigate to={destino} replace />
    );
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
      <LimiteDeErro area="a tela de entrada">
        <div className="mb-8">
          <LogoDeca className="h-9" />
          <p className="mt-2 text-sm text-conteudo-secundario">MS3 Capital · Créditos de ICMS</p>
        </div>

        <Cartao
          como="section"
          titulo="Entrar"
          subtitulo="Acesse com o e-mail cadastrado na plataforma."
        >
          {motivoEncerramento !== null && erroDoEnvio === null && (
            <div className="mb-4">
              <Aviso tom="atencao">{motivoEncerramento}</Aviso>
            </div>
          )}

          {erroDoEnvio !== null && (
            <div className="mb-4">
              <ErroDaApi
                titulo="Não foi possível entrar"
                erro={erroDoEnvio}
                camposNoFormulario={CAMPOS_LOGIN}
              />
            </div>
          )}

          <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
            <Campo
              rotulo="E-mail"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoFocus
              obrigatorio
              erro={errors.emailAddress?.message}
              {...register('emailAddress')}
            />

            <Campo
              rotulo="Senha"
              type="password"
              autoComplete="current-password"
              obrigatorio
              erro={errors.password?.message}
              {...register('password')}
            />

            <Botao type="submit" carregando={isSubmitting} larguraTotal>
              {isSubmitting ? 'Entrando…' : 'Entrar'}
            </Botao>

            <p className="text-center text-sm">
              <Link
                to="/recuperar-senha"
                className="text-conteudo-secundario underline-offset-4 hover:text-acento-ciano hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
              >
                Esqueci minha senha
              </Link>
            </p>
          </form>
        </Cartao>

        <p className="mt-6 text-center text-sm text-conteudo-secundario">
          Ainda não tem acesso?{' '}
          <Link
            to="/cadastro"
            className="text-acento-ciano underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
          >
            Cadastre sua empresa
          </Link>
        </p>

        <p className="mt-2 text-center text-xs text-conteudo-sutil">
          Problemas para entrar? Fale com o administrador da sua empresa.
        </p>
      </LimiteDeErro>
    </main>
  );
}

/**
 * Destino guardado pela `RotaProtegida` quando ela interceptou a navegação.
 *
 * Só caminho interno: `//host` é URL protocolo-relativa e viraria redirecionamento
 * aberto — o clássico link de phishing que sai do domínio depois do login.
 */
export function destinoDeOrigem(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;

  const { de } = state as { de?: unknown };
  if (typeof de !== 'string') return null;

  return de.startsWith('/') && !de.startsWith('//') ? de : null;
}

/** O e-mail que a tela anterior mandou junto (a de criar senha, por exemplo). */
export function emailDeOrigem(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const { email } = state as { email?: unknown };
  return typeof email === 'string' && email.includes('@') ? email : null;
}
